import React, { useState, useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Trophy,
  MessageCircle,
  Users,
  Activity,
  Search,
  Star,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { generateForumDescription } from '../utils/forumDescriptions';
import { findSoftwareBySlug, createSoftwareSlug } from '../utils/urlHelpers';
import { isSponsorActive } from '../utils/sponsor';
import { formatStatDelta } from '../utils/forumStatChange';
import { usePageSeo } from '../hooks/usePageSeo';
import { softwareCanonical } from '../utils/seo';
import RisingForumsBlock from './RisingForumsBlock';
import SoftwareSummaryPanel from './SoftwareSummaryPanel';
import { withCacheBust } from '../utils/mediaUrl';
import {
  aggregateCategoryForums,
  latestForumUpdateIso,
} from '../utils/categoryAggregates';
import { buildSoftwareSeoParagraph } from '../utils/softwareAggregates';
import type { ForumBase } from '../types/forum';
import ForumCardWrapper from './ForumCardWrapper';

interface Props {
  darkMode: boolean;
}

const SoftwareForums: React.FC<Props> = ({ darkMode }) => {
  const { software } = useParams<{ software: string }>();
  const [forums, setForums] = useState<ForumBase[]>([]);
  const [allForums, setAllForums] = useState<ForumBase[]>([]);
  const [allForumsCount, setAllForumsCount] = useState(0);
  const [actualSoftwareName, setActualSoftwareName] = useState<string>('');
  const [softwareDescription, setSoftwareDescription] = useState<string | null>(null);
  const [websiteUrl, setWebsiteUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'score' | 'topics_count' | 'posts_count' | 'members_count' | 'founded_year'>(
    'members_count'
  );

  useEffect(() => {
    if (software) {
      fetchForums();
    }
  }, [software]);

  const marketSharePercent = useMemo(() => {
    if (allForumsCount <= 0 || forums.length <= 0) return null;
    return (forums.length / allForumsCount) * 100;
  }, [forums.length, allForumsCount]);

  const pageSeo = useMemo(() => {
    if (!actualSoftwareName || forums.length === 0) {
      return actualSoftwareName
        ? {
            title: `${actualSoftwareName} Kullanan Forumlar`,
            description: `${actualSoftwareName} yazılımını kullanan Türk forum siteleri listesi.`,
            keywords: `${actualSoftwareName}, forum yazılımı`,
            canonical: softwareCanonical(actualSoftwareName),
          }
        : null;
    }
    const agg = aggregateCategoryForums(forums);
    const desc = buildSoftwareSeoParagraph(
      actualSoftwareName,
      agg,
      latestForumUpdateIso(forums),
      softwareDescription,
      marketSharePercent
    );
    return {
      title: `${actualSoftwareName} Kullanan Forumlar`,
      description: desc.slice(0, 320),
      keywords: `${actualSoftwareName}, ${actualSoftwareName.toLowerCase()} forumları, forum yazılımı, forum listesi`,
      canonical: softwareCanonical(actualSoftwareName),
    };
  }, [actualSoftwareName, forums, softwareDescription, marketSharePercent]);

  usePageSeo(pageSeo, [pageSeo]);

  const fetchForums = async () => {
    try {
      const { data: allForums, error: allError } = await supabase
        .from('forums')
        .select('software')
        .not('software', 'is', null);

      if (allError) throw allError;

      const { data: allActiveForums, error: allActiveError } = await supabase
        .from('forums')
        .select('*')
        .eq('is_active', true);

      if (allActiveError) throw allActiveError;
      setAllForums(allActiveForums || []);
      setAllForumsCount(allActiveForums?.length ?? 0);

      const softwareList = [...new Set(allForums?.map((f) => f.software).filter(Boolean) || [])];
      const decodedSoftware = decodeURIComponent(software || '');
      const foundSoftware = findSoftwareBySlug(decodedSoftware, softwareList);
      const finalSoftwareName = foundSoftware || decodedSoftware;

      setActualSoftwareName(finalSoftwareName);

      try {
        const { data: swMeta } = await supabase
          .from('software')
          .select('description, website_url')
          .eq('name', finalSoftwareName)
          .maybeSingle();
        setSoftwareDescription(swMeta?.description ?? null);
        setWebsiteUrl(swMeta?.website_url ?? null);
      } catch {
        setSoftwareDescription(null);
        setWebsiteUrl(null);
      }

      const { data, error } = await supabase
        .from('forums')
        .select('*')
        .eq('software', finalSoftwareName)
        .eq('is_active', true);

      if (error) throw error;
      setForums(data || []);
    } catch (error) {
      console.error('Error fetching forums:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateScore = (forum: ForumBase): number => {
    const topicsScore = (forum.topics_count || 0) * 5;
    const postsScore = (forum.posts_count || 0) * 2;
    const membersScore = (forum.members_count || 0) * 10;
    return Math.round((topicsScore + postsScore + membersScore) / (forum.founded_year || new Date().getFullYear()));
  };

  const calculatePreviousScore = (forum: ForumBase): number => {
    const topicsScore = (forum.previous_month_topics_count ?? 0) * 5;
    const postsScore = (forum.previous_month_posts_count ?? 0) * 2;
    const membersScore = (forum.previous_month_members_count ?? 0) * 10;
    return Math.round((topicsScore + postsScore + membersScore) / (forum.founded_year || new Date().getFullYear()));
  };

  const getScoreChange = (forum: ForumBase): { change: number; percent: number } => {
    const currentScore = calculateScore(forum);
    const previousScore = calculatePreviousScore(forum);
    const change = currentScore - previousScore;
    const percent = previousScore > 0 ? (change / previousScore) * 100 : 0;
    return { change, percent };
  };

  const getRanking = (
    forum: ForumBase,
    list: ForumBase[],
    field: typeof sortBy
  ): number => {
    const sortedList = [...list].sort((a, b) => {
      if (field === 'score') return calculateScore(b) - calculateScore(a);
      if (field === 'founded_year') return (a.founded_year || 0) - (b.founded_year || 0);
      const valueA = (a[field] ?? 0) as number;
      const valueB = (b[field] ?? 0) as number;
      return valueB - valueA;
    });
    return sortedList.findIndex((f) => f.id === forum.id) + 1;
  };

  const filteredForums = forums
    .filter((forum) => forum.name.toLowerCase().includes(searchTerm.toLowerCase()))
    .sort((a, b) => {
      if (isSponsorActive(a) && !isSponsorActive(b)) return -1;
      if (!isSponsorActive(a) && isSponsorActive(b)) return 1;
      if (sortBy === 'score') return calculateScore(b) - calculateScore(a);
      if (sortBy === 'founded_year') return (a.founded_year || 0) - (b.founded_year || 0);
      const valueA = (a[sortBy] ?? 0) as number;
      const valueB = (b[sortBy] ?? 0) as number;
      return valueB - valueA;
    });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500" />
      </div>
    );
  }

  return (
    <div className={`min-h-screen overflow-x-hidden ${darkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
      <div className="container mx-auto px-3 sm:px-4 py-6 sm:py-8 max-w-7xl">
        <Link
          to="/software"
          className={`inline-flex items-center gap-2 mb-6 ${
            darkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <ArrowLeft size={20} />
          Yazılımlara Dön
        </Link>

        <header className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold mb-2 break-words">
            {actualSoftwareName} Kullanan Forumlar
          </h1>
          <p className={`${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            {actualSoftwareName} yazılımını kullanan {forums.length} forum listeleniyor
          </p>
          <Link
            to={`/yazilim/${createSoftwareSlug(actualSoftwareName)}/istatistikleri`}
            className={`inline-block mt-2 text-sm font-medium ${darkMode ? 'text-indigo-400 hover:text-indigo-300' : 'text-indigo-600 hover:text-indigo-800'}`}
          >
            {actualSoftwareName} istatistik özeti →
          </Link>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8 items-stretch">
          <RisingForumsBlock
            forums={forums}
            darkMode={darkMode}
            title={`${actualSoftwareName} — en çok büyüyenler`}
            limit={5}
            className="mb-0"
          />
          <SoftwareSummaryPanel
            softwareName={actualSoftwareName}
            softwareDescription={softwareDescription}
            websiteUrl={websiteUrl}
            forums={forums}
            darkMode={darkMode}
            marketSharePercent={marketSharePercent}
          />
        </div>

        <div className="mb-6 flex flex-col md:flex-row gap-4 md:items-center md:justify-between">
          <div className="relative w-full md:flex-1 md:max-w-md">
            <Search
              className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${
                darkMode ? 'text-gray-500' : 'text-gray-400'
              }`}
              size={20}
            />
            <input
              type="text"
              placeholder="Forum ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full pl-10 pr-4 py-2 rounded-lg border ${
                darkMode
                  ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500'
                  : 'border-gray-200'
              } focus:outline-none focus:ring-2 focus:ring-indigo-500`}
            />
          </div>

          <div className="flex flex-wrap gap-2 w-full md:w-auto">
            <button
              type="button"
              onClick={() => setSortBy('score')}
              className={`min-h-[44px] px-3 sm:px-4 py-2 rounded-lg flex items-center justify-center gap-1.5 flex-1 sm:flex-none text-sm ${
                sortBy === 'score'
                  ? darkMode ? 'bg-indigo-500 text-white' : 'bg-indigo-600 text-white'
                  : darkMode ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Trophy size={18} />
              Skor
            </button>
            <button
              type="button"
              onClick={() => setSortBy('topics_count')}
              className={`min-h-[44px] px-3 sm:px-4 py-2 rounded-lg flex items-center justify-center gap-1.5 flex-1 sm:flex-none text-sm ${
                sortBy === 'topics_count'
                  ? darkMode ? 'bg-indigo-500 text-white' : 'bg-indigo-600 text-white'
                  : darkMode ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <MessageCircle size={18} />
              Konu
            </button>
            <button
              type="button"
              onClick={() => setSortBy('posts_count')}
              className={`min-h-[44px] px-3 sm:px-4 py-2 rounded-lg flex items-center justify-center gap-1.5 flex-1 sm:flex-none text-sm ${
                sortBy === 'posts_count'
                  ? darkMode ? 'bg-indigo-500 text-white' : 'bg-indigo-600 text-white'
                  : darkMode ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Activity size={18} />
              İleti
            </button>
            <button
              type="button"
              onClick={() => setSortBy('members_count')}
              className={`min-h-[44px] px-3 sm:px-4 py-2 rounded-lg flex items-center justify-center gap-1.5 flex-1 sm:flex-none text-sm ${
                sortBy === 'members_count'
                  ? darkMode ? 'bg-indigo-500 text-white' : 'bg-indigo-600 text-white'
                  : darkMode ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Users size={18} />
              Üye
            </button>
          </div>
        </div>

        <div className="grid gap-6">
          {filteredForums.map((forum) => {
            const scoreChange = getScoreChange(forum);
            const categoryRank = getRanking(
              forum,
              allForums.filter((f) => f.category === forum.category),
              sortBy
            );
            const globalRank = getRanking(forum, allForums, sortBy);

            return (
              <ForumCardWrapper
                key={forum.id}
                to={`/forum/${forum.forum_id}`}
                forumId={forum.forum_id}
                darkMode={darkMode}
                adminMeta={forum}
                className={`block ${
                  darkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-white hover:bg-gray-50'
                } rounded-xl p-4 sm:p-6 shadow-sm transition-all duration-200`}
              >
                <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                  {forum.logo_url && (
                    <img
                      src={withCacheBust(forum.logo_url, forum.updated_at) ?? forum.logo_url}
                      alt=""
                      className="w-14 h-14 sm:w-16 sm:h-16 object-contain rounded-lg shrink-0"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-start gap-2 mb-2">
                      <h2 className="text-lg sm:text-xl font-semibold break-words min-w-0 flex-1">
                        {forum.name}
                      </h2>
                      {isSponsorActive(forum) && (
                        <span
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium shrink-0 ${
                            darkMode ? 'bg-amber-900/50 text-amber-300' : 'bg-amber-100 text-amber-600'
                          }`}
                        >
                          <Star size={14} className="fill-current" />
                          Sponsorlu
                        </span>
                      )}
                      <div
                        className={`flex flex-wrap items-center gap-x-2 gap-y-1 text-xs sm:text-sm w-full sm:w-auto ${
                          darkMode ? 'text-gray-400' : 'text-gray-600'
                        }`}
                      >
                        <span>Kategori #{categoryRank}</span>
                        <span>•</span>
                        <span>Genel #{globalRank}</span>
                      </div>
                    </div>
                    <p className={`${darkMode ? 'text-gray-300' : 'text-gray-600'} text-sm mb-4`}>
                      {forum.description ||
                        generateForumDescription(
                          forum.forum_id,
                          forum.name,
                          forum.category,
                          forum.members_count,
                          forum.posts_count,
                          forum.topics_count
                        )}
                    </p>
                    <div className="flex flex-wrap gap-x-6 gap-y-3">
                      <div className="flex items-center gap-2">
                        <Trophy className="text-amber-500 shrink-0" size={18} />
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium">
                            {calculateScore(forum).toLocaleString('tr-TR')}
                          </span>
                          {scoreChange.change !== 0 && (
                            <span
                              className={`flex items-center gap-1 text-sm ${
                                scoreChange.change > 0
                                  ? 'text-green-500'
                                  : scoreChange.change < 0
                                    ? 'text-red-500'
                                    : darkMode
                                      ? 'text-gray-400'
                                      : 'text-gray-600'
                              }`}
                            >
                              {scoreChange.change > 0 ? (
                                <ArrowUpRight size={14} />
                              ) : scoreChange.change < 0 ? (
                                <ArrowDownRight size={14} />
                              ) : (
                                <Minus size={14} />
                              )}
                              <span>
                                {Math.abs(scoreChange.change).toLocaleString('tr-TR')} (
                                {scoreChange.percent > 0 ? '+' : ''}
                                {scoreChange.percent.toFixed(1)}%)
                              </span>
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <MessageCircle className="text-purple-500 shrink-0" size={18} />
                        <div className="flex items-center gap-2 flex-wrap">
                          <span>{forum.topics_count.toLocaleString('tr-TR')}</span>
                          {formatStatDelta(forum.topics_count, forum.previous_month_topics_count) && (
                            <span
                              className={`text-sm ${
                                (forum.topics_count - (forum.previous_month_topics_count ?? 0)) > 0
                                  ? 'text-green-500'
                                  : 'text-red-500'
                              }`}
                            >
                              {formatStatDelta(forum.topics_count, forum.previous_month_topics_count)}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Activity className="text-green-500 shrink-0" size={18} />
                        <div className="flex items-center gap-2 flex-wrap">
                          <span>{forum.posts_count.toLocaleString('tr-TR')}</span>
                          {formatStatDelta(forum.posts_count, forum.previous_month_posts_count) && (
                            <span
                              className={`text-sm ${
                                (forum.posts_count - (forum.previous_month_posts_count ?? 0)) > 0
                                  ? 'text-green-500'
                                  : 'text-red-500'
                              }`}
                            >
                              {formatStatDelta(forum.posts_count, forum.previous_month_posts_count)}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Users className="text-blue-500 shrink-0" size={18} />
                        <div className="flex items-center gap-2 flex-wrap">
                          <span>{forum.members_count.toLocaleString('tr-TR')}</span>
                          {formatStatDelta(forum.members_count, forum.previous_month_members_count) && (
                            <span
                              className={`text-sm ${
                                (forum.members_count - (forum.previous_month_members_count ?? 0)) > 0
                                  ? 'text-green-500'
                                  : 'text-red-500'
                              }`}
                            >
                              {formatStatDelta(forum.members_count, forum.previous_month_members_count)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </ForumCardWrapper>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default SoftwareForums;
